import { useEffect, type RefObject } from 'react';

/**
 * Shared behaviour for full-screen overlays (the ⌘K palette, the phone's More
 * sheet): one body scroll lock however many are open, and focus kept inside
 * the overlay, then handed back to whatever had it before.
 */

let scrollLocks = 0;
let overflowBeforeLock = '';

/**
 * Counted, so two overlays closing in the same commit cannot restore each
 * other's `hidden` and leave the page unscrollable.
 */
export function useBodyScrollLock(): void {
  useEffect(() => {
    if (scrollLocks === 0) {
      overflowBeforeLock = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    scrollLocks += 1;
    return () => {
      scrollLocks -= 1;
      if (scrollLocks === 0) document.body.style.overflow = overflowBeforeLock;
    };
  }, []);
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Moves focus into the container on mount (`initial`, else its first
 * focusable element), keeps Tab and Shift+Tab cycling inside it, and on
 * unmount returns focus to the element that had it when the overlay opened.
 */
export function useFocusTrap(containerRef: RefObject<HTMLElement | null>, initial?: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    (initial?.current ?? container.querySelector<HTMLElement>(FOCUSABLE))?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const focusables = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', onKeyDown);
    return () => {
      container.removeEventListener('keydown', onKeyDown);
      if (previous?.isConnected) previous.focus();
    };
  }, [containerRef, initial]);
}
